// ============================================================
// TMR Portal Admin — ler uma página da rota /export de uma lista
// ============================================================
import { api } from '@tmr/api-client';
import type { Paged } from '@tmr/api-client';

/**
 * Rows per request while walking an export. Matches the backend's `PageRequest.MaxExportPageSize` — asking
 * for more is clamped anyway, and asking for less would only multiply the round-trips.
 */
export const EXPORT_PAGE_SIZE = 1000;

/**
 * Builds the `fetchPage` an {@link import('./types').ExportSpec} needs, for a list's `/export` route.
 *
 * It takes the path rather than owning one because every list has its own — but it is called from the
 * `data/` layer of each area, never from a screen, so endpoints stay where the rest of them live
 * (01 §16: no `fetch` scattered across screens).
 *
 * @param path The list's export route, e.g. `/companies/export`.
 * @param query The screen's active filters, exactly as the list itself sends them.
 */
export function exportPageReader<T>(
  path: string,
  query: Record<string, unknown>,
): (page: number) => Promise<Paged<T>> {
  return (page) => api.get<Paged<T>>(path, { query: { ...query, page, pageSize: EXPORT_PAGE_SIZE } });
}

/**
 * Presents an **unpaged** endpoint as a one-page walk.
 *
 * Some lists are not paged and should not be — an Empresa's rates, its receiving accounts, the global
 * template: they are short by construction, and the screens read them whole. Rather than teach the kit
 * about two kinds of source, they arrive here as a single page, and the walk ends after one request.
 *
 * @param read Reads the whole list.
 */
export function singlePageReader<T>(read: () => Promise<T[]>): (page: number) => Promise<Paged<T>> {
  return async () => {
    const items = await read();
    return { items, page: 1, pageSize: items.length, totalCount: items.length };
  };
}
